/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { FormikErrors, useFormik } from 'formik';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { IMaskInput } from 'react-imask';
import { ROUTES } from 'routes/constants';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { cpfValidation } from 'utils/yup_cpf_validation';

import {
  ICreateCuffaFamilyDataDto,
  CuffaGenderEnum,
  CuffaMaritalStatusEnum,
  getMaritalStatusEnum,
  getGenderEnum,
  getMaritalStatusFormValue,
  getGenderFormValue,
  TextMaskTelefone,
  TextMaskCpf,
  estadoCivilComConjuge,
  isValidCpf,
  cleanCpf,
  validationSchema as sharedValidationSchema,
} from '../common';


const initialValues = {
  nomeUnidadeFamiliar: '',
  nomeComunidade: '',
  nomeInformante: '',
  cpf: '',
  dataNascimento: '',
  telefone: '',
  genero: '',
  estadoCivil: '',
  nomeConjuge: '',
  generoConjuge: '',
  dataNascimentoConjuge: '',
  qtdPessoas: '',
};

const CONFLICT_REGEX = /409|conflit|conflito|existe uma coleta|já existe/i;

function buildPayload(values: any): ICreateCuffaFamilyDataDto {
  return {
    family_unit_name: values.nomeUnidadeFamiliar,
    community_name: values.nomeComunidade,
    reference_year: new Date().getFullYear(),
    informant_full_name: values.nomeInformante,
    informant_marital_status: getMaritalStatusEnum(values.estadoCivil),
    informant_birth_date: values.dataNascimento,
    informant_gender: getGenderEnum(values.genero),
    informant_cpf: values.cpf,
    informant_phone: values.telefone,
    family_members_count: parseInt(values.qtdPessoas, 10),
    ...(estadoCivilComConjuge.includes(values.estadoCivil) && {
      spouse_full_name: values.nomeConjuge,
      spouse_birth_date: values.dataNascimentoConjuge,
      spouse_gender: getGenderEnum(values.generoConjuge),
    }),
  };
}

function getCurrentUuidFromContext(urlUuid?: string | undefined): string | undefined {
  return urlUuid ?? useCuffaStore.getState().collectionUuid ?? undefined;
}

async function checkExistingUuidByCpf(cpf: string): Promise<string | undefined> {
  try {
    const res = await CuffaAPI.checkExistingCpf(cpf);
    return res?.dados?.active_collection_uuid ?? res?.dados?.uuid ?? undefined;
  } catch {
    return undefined;
  }
}

function mapApiToPartial(d: any) {
  return {
    nomeComunidade: d?.community_name || '',
    nomeUnidadeFamiliar: d?.family_unit_name || '',
    nomeInformante: d?.informant_full_name || '',
    dataNascimento: d?.informant_birth_date || '',
    cpf: d?.informant_cpf || '',
    genero: getGenderFormValue(d?.informant_gender),
    telefone: d?.informant_phone || '',
    nomeConjuge: d?.spouse_full_name || '',
    estadoCivil: getMaritalStatusFormValue(d?.informant_marital_status),
    dataNascimentoConjuge: d?.spouse_birth_date || '',
    generoConjuge: getGenderFormValue(d?.spouse_gender),
    qtdPessoas: String(d?.family_members_count || ''),
  };
}

async function persistFromApiOrForm(
  uuid: string,
  salvarDadosParciais: (d: any) => void,
  valuesFallback: any
) {
  try {
    const updated = await CuffaAPI.getDataByID(uuid);
    const d = updated?.dados;
    if (d) {
      salvarDadosParciais({ identificacaoFamiliar: mapApiToPartial(d) });
      return;
    }
  } catch {
    // ignora
  }
  salvarDadosParciais({ identificacaoFamiliar: valuesFallback });
}

async function updateFlow(
  uuid: string,
  payload: ICreateCuffaFamilyDataDto,
  salvarDadosParciais: (d: any) => void | Promise<void>,
  valuesFallback: any
) {
  await CuffaAPI.updateFamilyData(uuid, payload);
  await persistFromApiOrForm(uuid, salvarDadosParciais, valuesFallback);
  return uuid;
}


async function createFlowOrResolveConflict(
  values: any,
  payload: ICreateCuffaFamilyDataDto,
  ui: {
    setFieldError: (n: string, m?: string) => void | Promise<void>;
    setFieldTouched: (
      n: string,
      t?: boolean,
      v?: boolean
    ) => void | Promise<void | FormikErrors<any>>;
    setSnackbarMessage: (m: string) => void | Promise<void>;
    setOpenErrorSnackbar: (b: boolean) => void | Promise<void>;
  },
  salvarDadosParciais: (d: any) => void | Promise<void>
): Promise<string | undefined> {
  const preUuid = await checkExistingUuidByCpf(values.cpf);
  if (preUuid) {
    await updateFlow(preUuid, payload, salvarDadosParciais, values);
    ui.setSnackbarMessage('Dados da família atualizados com sucesso!');
    return preUuid;
  }

  try {
    const created = await CuffaAPI.createManualCollection(payload);
    const statusText = created?.Status;
    const msgText = created?.Mensagem ?? '';

    if (statusText === 'ERRO' && CONFLICT_REGEX.test(msgText)) {
      const conflictUuid = await checkExistingUuidByCpf(values.cpf);
      if (!conflictUuid) {
        const msg = msgText || 'Já existe uma coleta para este CPF no ano de referência.';
        ui.setFieldError('cpf', msg);
        ui.setFieldTouched('cpf', true, false);
        ui.setSnackbarMessage(msg);
        ui.setOpenErrorSnackbar(true);
        return undefined;
      }
      await updateFlow(conflictUuid, payload, salvarDadosParciais, values);
      ui.setSnackbarMessage('Dados da família atualizados com sucesso!');
      return conflictUuid;
    }

    const newUuid = created?.dados?.uuid;
    if (!newUuid) {
      throw new Error(msgText || 'UUID não retornado pela API após criação.');
    }

    salvarDadosParciais({ identificacaoFamiliar: values });
    ui.setSnackbarMessage('Família cadastrada com sucesso!');
    return newUuid;
  } catch (e: any) {
    const status = e?.response?.status;
    const apiMsg =
      e?.response?.data?.Mensagem || e?.response?.data?.message || e?.message || '';

    if (status === 409 || CONFLICT_REGEX.test(apiMsg)) {
      const conflictUuid = await checkExistingUuidByCpf(values.cpf);
      if (!conflictUuid) {
        const msg = apiMsg || 'Já existe uma coleta para este CPF no ano de referência.';
        ui.setFieldError('cpf', msg);
        ui.setFieldTouched('cpf', true, false);
        ui.setSnackbarMessage(msg);
        ui.setOpenErrorSnackbar(true);
        return undefined;
      }
      await updateFlow(conflictUuid, payload, salvarDadosParciais, values);
      ui.setSnackbarMessage('Dados da família atualizados com sucesso!');
      return conflictUuid;
    }
    throw e;
  }
}

function navigateAfterSuccess(
  effectiveUuid: string,
  advanceNext: React.MutableRefObject<boolean>,
  setOpenSuccessSnackbar: (b: boolean) => void,
  setCollectionUuid: (id: string) => void,
  navigate: (path: string) => void
) {
  setOpenSuccessSnackbar(true);
  setCollectionUuid(effectiveUuid);
  if (advanceNext.current) {
    navigate(ROUTES.VISITAS_INSTITUICOES_CUFFA_INCLUIR.replace(':uuid', effectiveUuid));
  } else {
    navigate(ROUTES.CUFFA);
  }
}

function showGenericError(
  error: any,
  setSnackbarMessage: (m: string) => void,
  setOpenErrorSnackbar: (b: boolean) => void
) {
  setSnackbarMessage(
    error?.response?.data?.Mensagem ||
    error?.message ||
    'Ocorreu um erro ao salvar os dados.'
  );
  setOpenErrorSnackbar(true);
}
const validateBirthDate = (value: string | undefined): boolean => {
  if (!value) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value);
  if (isNaN(date.getTime())) return false;
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) return false;
  if (date > new Date()) return false;
  return true;
};

const validationSchema = Yup.object().shape({
  nomeUnidadeFamiliar: Yup.string().max(100),
  cpf: cpfValidation('Campo obrigatório'),
  nomeComunidade: Yup.string().max(100).required('Campo obrigatório'),
  nomeInformante: Yup.string().required('Campo obrigatório'),
  genero: Yup.string().required('Campo obrigatório'),
  estadoCivil: Yup.string().required('Campo obrigatório'),
  dataNascimento: Yup.string()
    .required('Campo obrigatório')
    .test('valid-year', 'O ano deve estar entre 1900 e o ano atual', validateBirthDate)
    .test('not-future', 'A data não pode ser futura', (value) => {
      if (!value) return false;
      const date = new Date(value);
      return date <= new Date();
    }),
  telefone: Yup.string().required('Campo obrigatório'),
  qtdPessoas: Yup.number()
    .integer('Deve ser um número inteiro')
    .typeError("Deve ser um número")
    .min(1, 'A quantidade deve ser no mínimo 1')
    .max(50, 'A quantidade deve ser no máximo 50')
    .required('Campo obrigatório'),
  nomeConjuge: Yup.string().when('estadoCivil', {
    is: (val: string) => estadoCivilComConjuge.includes(val),
    then: (schema) => schema.required('Campo obrigatório'),
  }),
  dataNascimentoConjuge: Yup.string().when('estadoCivil', {
    is: (val: string) => estadoCivilComConjuge.includes(val),
    then: (schema) => schema
      .required('Campo obrigatório')
      .test('valid-year', 'O ano deve estar entre 1900 e o ano atual', validateBirthDate)
      .test('not-future', 'A data não pode ser futura', (value) => {
        if (!value) return false;
        const date = new Date(value);
        return date <= new Date();
      }),
  }),
  generoConjuge: Yup.string().when('estadoCivil', {
    is: (val: string) => estadoCivilComConjuge.includes(val),
    then: (schema) => schema.required('Campo obrigatório'),
  }),
});

export const DadosFamiliaIncluir: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const advanceNext = useRef(false);

  const { dadosParciais, salvarDadosParciais, setCollectionUuid, limparDadosParciais } = useCuffaStore();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (uuid) {
        try {
          const response = await CuffaAPI.getDataByID(uuid);
          if (response && response.dados) {
            const apiData = response.dados;
            const mappedData = {
              nomeComunidade: apiData.community_name || '',
              nomeInformante: apiData.informant_full_name || '',
              dataNascimento: apiData.informant_birth_date || '',
              genero: getGenderFormValue(apiData.informant_gender),
              cpf: apiData.informant_cpf || '',
              telefone: apiData.informant_phone || '',
              estadoCivil: getMaritalStatusFormValue(apiData.informant_marital_status),
              nomeConjuge: apiData.spouse_full_name || '',
              nomeUnidadeFamiliar: apiData.family_unit_name || '',
              dataNascimentoConjuge: apiData.spouse_birth_date || '',
              qtdPessoas: String(apiData.family_members_count || ''),
              generoConjuge: getGenderFormValue(apiData.spouse_gender),
            };
            formik.setValues(mappedData);
          } else {
            setOpenErrorSnackbar(true);
            setSnackbarMessage('Não foi possível encontrar os dados da família.');

          }
        } catch (error) {
          setSnackbarMessage('Erro ao carregar os dados da família.');
          console.error("Erro ao buscar dados da família:", error);
          setOpenErrorSnackbar(true);

        }
      } else {
        const valoresIniciais = dadosParciais?.identificacaoFamiliar || initialValues;
        formik.setValues(valoresIniciais);
      }
      setLoading(false);
    };

    loadData();
  }, [uuid]);



  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validationSchema,
    validateOnBlur: true,

    onSubmit: async (values, helpers) => {
      const { setFieldError, setFieldTouched } = helpers;
      setSaving(true);

      const payload = buildPayload(values);
      const currentUuid = getCurrentUuidFromContext(uuid);

      try {
        let effectiveUuid: string | undefined;

        if (currentUuid) {
          try {
            await updateFlow(currentUuid, payload, salvarDadosParciais, values);
            setSnackbarMessage('Dados da família atualizados com sucesso!');
            effectiveUuid = currentUuid;
          } catch (updateError: any) {
            const apiMsg = updateError?.response?.data?.Mensagem || updateError?.response?.data?.message || '';
            if (updateError?.response?.status === 400 && apiMsg.includes('family_members_count')) {
              const errorMsg = apiMsg.includes('must not be greater than 50')
                ? 'A quantidade deve ser no máximo 50'
                : apiMsg.includes('must not be less than 1')
                  ? 'A quantidade deve ser no mínimo 1'
                  : 'Quantidade de pessoas inválida';
              setFieldError('qtdPessoas', errorMsg);
              setFieldTouched('qtdPessoas', true, false);
              setSnackbarMessage(errorMsg);
              setOpenErrorSnackbar(true);
              return;
            }
            throw updateError;
          }
        } else {
          effectiveUuid = await createFlowOrResolveConflict(
            values,
            payload,
            { setFieldError, setFieldTouched, setSnackbarMessage, setOpenErrorSnackbar },
            salvarDadosParciais
          );
          if (!effectiveUuid) return;
        }

        navigateAfterSuccess(
          effectiveUuid,
          advanceNext,
          setOpenSuccessSnackbar,
          setCollectionUuid,
          navigate
        );
      } catch (error: any) {
        console.error('Erro ao salvar dados da família:', error);
        const apiMsg = error?.response?.data?.Mensagem || error?.response?.data?.message || error?.message || '';

        if (error?.response?.status === 400 && apiMsg.includes('family_members_count')) {
          const errorMsg = apiMsg.includes('must not be greater than 50')
            ? 'A quantidade deve ser no máximo 50'
            : apiMsg.includes('must not be less than 1')
              ? 'A quantidade deve ser no mínimo 1'
              : 'Quantidade de pessoas inválida';
          setFieldError('qtdPessoas', errorMsg);
          setFieldTouched('qtdPessoas', true, false);
          setSnackbarMessage(errorMsg);
          setOpenErrorSnackbar(true);
        } else {
          showGenericError(error, setSnackbarMessage, setOpenErrorSnackbar);
        }
      } finally {
        setSaving(false);
        advanceNext.current = false;
      }
    },

  });


  const handleSave = () => {
    advanceNext.current = false;
    formik.handleSubmit();
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpenSuccessSnackbar(false);
    setOpenErrorSnackbar(false);
  };

  const handleAdvance = () => {
    advanceNext.current = true;
    formik.handleSubmit();
  };



  const handleQtdPessoasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || /^[0-9\b]+$/.test(val)) {
      formik.setFieldValue('qtdPessoas', val);
    }
  };

  const handleCpfBlur = (e: React.SyntheticEvent | any) => {
    formik.handleBlur(e);
    try {
      formik.setFieldTouched('cpf', true, true);
      formik.validateField('cpf');
    } catch (err) {
      console.error('Erro ao validar CPF no blur:', err);
    }
  };

  const handleChangeUpperCase = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value.toUpperCase();
    formik.handleChange(event);
  };

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Cadastro de Unidades Familiares" showBackButton={true} />
        <Box display="flex" width="100%" alignItems="center" justifyContent="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Cadastro de Unidades Familiares" showBackButton={true} />
      <Box width="100%" justifyContent="center" display="flex" margin="2rem">
        <Box sx={{ padding: '2rem', maxWidth: '1152px', width: '100%' }}>
          <ContainerForm
            title="Dados da Familia"
            subtitle="Preencha os dados do informante e da composição familiar."
            width="100%"
          >
            <Grid container spacing={2} marginTop="1rem" marginBottom="1rem">
              <Grid item md={6} xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Nome da Unidade Familiar
                </Typography>
                <TextField name="nomeUnidadeFamiliar" value={formik.values.nomeUnidadeFamiliar} onChange={handleChangeUpperCase} onBlur={formik.handleBlur} error={!!formik.errors.nomeUnidadeFamiliar && formik.touched.nomeUnidadeFamiliar} helperText={formik.touched.nomeUnidadeFamiliar && formik.errors.nomeUnidadeFamiliar} inputProps={{ maxLength: 100 }} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Nome da Comunidade/Assentamento *
                </Typography>
                <TextField name="nomeComunidade" value={formik.values.nomeComunidade} onChange={handleChangeUpperCase} onBlur={formik.handleBlur} error={!!formik.errors.nomeComunidade && formik.touched.nomeComunidade} helperText={formik.touched.nomeComunidade && formik.errors.nomeComunidade} inputProps={{ maxLength: 100 }} fullWidth required />
              </Grid>
            </Grid>
            <Grid container spacing={2} marginTop="0.5rem" marginBottom="1rem" >
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Nome completo *
                </Typography>
                <TextField name="nomeInformante" value={formik.values.nomeInformante} onChange={handleChangeUpperCase} onBlur={formik.handleBlur} error={!!formik.errors.nomeInformante && formik.touched.nomeInformante} helperText={formik.touched.nomeInformante && formik.errors.nomeInformante} fullWidth required />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  CPF *
                </Typography>
                <TextField name="cpf" value={formik.values.cpf} onChange={formik.handleChange} onBlur={handleCpfBlur} placeholder='000.000.000-00' error={!!formik.errors.cpf && formik.touched.cpf} helperText={formik.touched.cpf && formik.errors.cpf} fullWidth required InputProps={{ inputComponent: TextMaskCpf as any }} inputProps={{ 'data-testid': 'cpf-input' }} />
              </Grid>
            </Grid>
            <Grid container spacing={2} marginBottom="1rem">
              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold" variant="subtitle2" gutterBottom>
                  Data de nascimento *
                </Typography>
                <TextField type="date" name="dataNascimento" value={formik.values.dataNascimento} onChange={formik.handleChange} onBlur={formik.handleBlur} error={!!formik.errors.dataNascimento && formik.touched.dataNascimento} helperText={formik.touched.dataNascimento && formik.errors.dataNascimento} fullWidth InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Telefone para contato *
                </Typography>
                <TextField name="telefone" value={formik.values.telefone} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder='(65) 00000-0000' error={!!formik.errors.telefone && formik.touched.telefone} helperText={formik.touched.telefone && formik.errors.telefone} fullWidth required InputProps={{ inputComponent: TextMaskTelefone as any }} />
              </Grid>
            </Grid>
            <Box marginBottom="1rem">
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Gênero *
              </Typography>
              <RadioGroup row name="genero" value={formik.values.genero} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                {['Masculino', 'Feminino', 'Prefiro não responder'].map((item) => (
                  <FormControlLabel key={item} value={item} control={<Radio />} label={item} />
                ))}
              </RadioGroup>
              {formik.touched.genero && formik.errors.genero && (<Typography color="error" variant="caption">{formik.errors.genero}</Typography>)}
            </Box>
            <Box marginBottom="1rem">
              <Typography fontWeight="bold" variant="subtitle2" gutterBottom>
                Estado civil *
              </Typography>
              <TextField select name="estadoCivil" value={formik.values.estadoCivil} onChange={formik.handleChange} onBlur={formik.handleBlur} error={!!formik.errors.estadoCivil && formik.touched.estadoCivil} helperText={formik.touched.estadoCivil && formik.errors.estadoCivil} fullWidth required SelectProps={{ native: true }}>
                <option value=""></option>
                <option value="SOLTEIRA(O)">SOLTEIRA(O)</option>
                <option value="CASADA(O)">CASADA(O)</option>
                <option value="UNIÃO ESTÁVEL">UNIÃO ESTÁVEL</option>
                <option value="VIÚVA(O)">VIÚVA(O)</option>
                <option value="SEPARADA(O)/DIVORCIADA(O)">SEPARADA(O)/DIVORCIADA(O)</option>
              </TextField>
            </Box>
            {estadoCivilComConjuge.includes(formik.values.estadoCivil) && (
              <>
                <Typography marginTop="2rem" variant="h6" fontWeight="bold">Dados do(a) cônjuge</Typography>
                <Grid container spacing={2} marginBottom="1rem" marginTop="0.5rem">
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight="bold" variant="subtitle2" gutterBottom>Nome completo do(a) cônjuge *</Typography>
                    <TextField name="nomeConjuge" value={formik.values.nomeConjuge} onChange={handleChangeUpperCase} onBlur={formik.handleBlur} error={!!formik.errors.nomeConjuge && formik.touched.nomeConjuge} helperText={formik.touched.nomeConjuge && formik.errors.nomeConjuge} fullWidth required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Data de nascimento do(a) cônjuge *</Typography>
                    <TextField name="dataNascimentoConjuge" type="date" value={formik.values.dataNascimentoConjuge} onChange={formik.handleChange} onBlur={formik.handleBlur} error={!!formik.errors.dataNascimentoConjuge && formik.touched.dataNascimentoConjuge} helperText={formik.touched.dataNascimentoConjuge && formik.errors.dataNascimentoConjuge} fullWidth InputLabelProps={{ shrink: true }} required />
                  </Grid>
                </Grid>
                <Box marginBottom="1rem">
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Gênero do(a) cônjuge *</Typography>
                  <RadioGroup row name="generoConjuge" value={formik.values.generoConjuge} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                    {['Masculino', 'Feminino', 'Prefiro não responder'].map((item) => (<FormControlLabel key={item} value={item} control={<Radio />} label={item} />))}
                  </RadioGroup>
                  {formik.touched.generoConjuge && formik.errors.generoConjuge && (<Typography color="error" variant="caption">{formik.errors.generoConjuge}</Typography>)}
                </Box>
              </>
            )}
            <Box marginBottom="1rem" maxWidth={300}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Quantidade de pessoas na família *
              </Typography>
              <TextField name="qtdPessoas" value={formik.values.qtdPessoas} onChange={handleQtdPessoasChange} onBlur={formik.handleBlur} error={!!formik.errors.qtdPessoas && formik.touched.qtdPessoas} helperText={formik.touched.qtdPessoas && formik.errors.qtdPessoas} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} fullWidth required />
            </Box>
            <Box marginTop="2rem" display="flex" gap="1rem" justifyContent="center" >
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.CUFFA)} disabled={saving}>Cancelar</Button>
              <Button variant="outlined" color="secondary" onClick={handleSave} disabled={saving} data-testid="cpf-save">
                {saving && !advanceNext.current ? <CircularProgress size={20} color="inherit" /> : 'Salvar e Sair'}
              </Button>
              <Button variant="contained" color="primary" onClick={handleAdvance} disabled={saving} data-testid="cpf-advance">
                {saving && advanceNext.current ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                Salvar e Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
      <Snackbar open={openSuccessSnackbar} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ horizontal: 'center', vertical: 'top', }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </MenuProvider>
  );
};

// Test helpers exported to allow unit tests to exercise internal logic without
// impacting runtime behavior. These are only used in tests.
export const __testHelpers = {
  getMaritalStatusEnum,
  getGenderEnum,
  getMaritalStatusFormValue,
  getGenderFormValue,
  isValidCpf,
  buildPayload,
  getCurrentUuidFromContext,
  checkExistingUuidByCpf,
  mapApiToPartial,
  persistFromApiOrForm,
  updateFlow,
  createFlowOrResolveConflict,
  navigateAfterSuccess,
  showGenericError,
  TextMaskCpf,
  TextMaskTelefone,
};

// Re-export enums and form helper components for unit tests
export { CuffaGenderEnum, CuffaMaritalStatusEnum };
